import { apiService } from './api';

export interface VCPPattern {
  symbol: string;
  stock_id: string;
  stock_name?: string;
  detection_date: string;
  contractions: Array<{
    high: number;
    low: number;
    high_date: string;
    low_date: string;
    range_pct: number;
    reduction_from_prev: number;
  }>;
  num_contractions: number;
  contraction_percentages: number[];
  volume_dry_up_score: number;
  pivot_price: number;
  pivot_date: string | null;
  price_above_50ma: boolean;
  price_above_150ma: boolean;
  price_above_200ma: boolean;
  ma_200_slope_positive: boolean;
  in_stage2_uptrend: boolean;
  rs_rating: number;
  pattern_quality_score: number;
  breakout_probability: number;
  current_price: number;
  distance_from_pivot_pct: number;
  is_near_breakout: boolean;
}

export interface VCPScanResponse {
  success: boolean;
  count: number;
  candidates: VCPPattern[];
  filters?: {
    min_quality: number;
    stage2_only: boolean;
    near_breakout_only: boolean;
    min_rs: number;
  };
  error?: string;
}

export interface VCPDetectResponse {
  success: boolean;
  pattern_found: boolean;
  pattern?: VCPPattern;
  message?: string;
  error?: string;
}

export interface BacktestSummary {
  total_patterns: number;
  breakouts_occurred: number;
  no_breakouts: number;
  successful_trades: number;
  stopped_out: number;
  success_rate: number;
  breakout_rate: number;
  avg_gain_on_success: number;
  avg_loss_on_failure: number;
  max_gain: number;
  max_loss: number;
  profit_factor: number | string;
  expectancy: number;
  avg_quality_score: number;
  avg_days_to_breakout: number;
  stage2_patterns: number;
  stage2_success_rate: number;
  hit_target_count: number;
  hit_target_rate: number;
  stocks_processed: number;
  start_date: string;
  end_date: string;
  min_quality_score: number;
}

export interface BacktestTrade {
  symbol: string;
  detection_date: string;
  pattern_quality: number;
  num_contractions: number;
  in_stage2: boolean;
  rs_rating: number;
  pivot_price: number;
  volume_dry_up_score: number;
  breakout_occurred: boolean;
  breakout_date: string | null;
  entry_price: number | null;
  max_gain_pct: number;
  max_loss_pct: number;
  final_return_pct: number;
  days_to_breakout: number | null;
  stopped_out: boolean;
  hit_target: boolean;
  holding_days: number;
}

export interface BestPerformingStock {
  symbol: string;
  total_patterns: number;
  successful_trades: number;
  success_rate: number;
  total_return: number;
  avg_return: number;
  avg_quality: number;
}

export interface QualityScoreAnalysis {
  [range: string]: {
    count: number;
    success_rate: number;
    avg_return: number;
  };
}

export interface BacktestResponse {
  success: boolean;
  backtest_id?: string;
  summary?: BacktestSummary;
  best_performing_stocks?: BestPerformingStock[];
  quality_score_analysis?: QualityScoreAnalysis;
  sample_trades?: BacktestTrade[];
  error?: string;
}

export interface BacktestResult {
  id: string;
  backtest_date: string;
  start_date: string;
  end_date: string;
  min_quality_score: number;
  total_patterns_found: number;
  successful_breakouts: number;
  failed_breakouts: number;
  success_rate: number;
  avg_gain_on_success: number | null;
  avg_loss_on_failure: number | null;
  max_gain: number | null;
  max_loss: number | null;
  avg_days_to_breakout: number | null;
  profit_factor: number | null;
  expectancy: number | null;
  detailed_results: BacktestTrade[] | null;
  created_at: string;
}

export interface BacktestResultsResponse {
  success: boolean;
  count: number;
  results: BacktestResult[];
  error?: string;
}

export interface VCPCriteria {
  name: string;
  description: string;
  key_elements: Array<{
    name: string;
    description: string;
  }>;
  entry_rules: string[];
  success_factors: string[];
}

export interface VCPCriteriaResponse {
  success: boolean;
  criteria: VCPCriteria;
}

export interface SavedPatternsResponse {
  success: boolean;
  count: number;
  patterns: VCPPattern[];
  error?: string;
}

class VCPService {
  async scanForVCP(options: {
    min_quality?: number;
    stage2_only?: boolean;
    near_breakout_only?: boolean;
    min_rs?: number;
    limit?: number;
  } = {}): Promise<VCPScanResponse> {
    return apiService.post<VCPScanResponse>('/vcp/scan', options);
  }

  async detectVCPForStock(symbol: string): Promise<VCPDetectResponse> {
    return apiService.get<VCPDetectResponse>(`/vcp/detect/${symbol}`);
  }

  async getVCPCandidates(options: {
    min_quality?: number;
    stage2_only?: boolean;
    near_breakout?: boolean;
    min_rs?: number;
    limit?: number;
  } = {}): Promise<VCPScanResponse> {
    const params = new URLSearchParams();
    if (options.min_quality !== undefined) params.append('min_quality', options.min_quality.toString());
    if (options.stage2_only !== undefined) params.append('stage2_only', options.stage2_only.toString());
    if (options.near_breakout !== undefined) params.append('near_breakout', options.near_breakout.toString());
    if (options.min_rs !== undefined) params.append('min_rs', options.min_rs.toString());
    if (options.limit !== undefined) params.append('limit', options.limit.toString());
    
    return apiService.get<VCPScanResponse>(`/vcp/candidates?${params.toString()}`);
  }

  async runBacktest(options: {
    start_date: string;
    end_date: string;
    min_quality?: number;
    max_stocks?: number;
    symbols?: string[];
  }): Promise<BacktestResponse> {
    return apiService.post<BacktestResponse>('/vcp/backtest', options);
  }

  async getBacktestResults(limit: number = 10): Promise<BacktestResultsResponse> {
    return apiService.get<BacktestResultsResponse>(`/vcp/backtest/results?limit=${limit}`);
  }

  async getBacktestResult(resultId: string): Promise<{ success: boolean; result: BacktestResult; error?: string }> {
    return apiService.get(`/vcp/backtest/results/${resultId}`);
  }

  async getSavedPatterns(options: {
    days?: number;
    min_quality?: number;
    limit?: number;
  } = {}): Promise<SavedPatternsResponse> {
    const params = new URLSearchParams();
    if (options.days !== undefined) params.append('days', options.days.toString());
    if (options.min_quality !== undefined) params.append('min_quality', options.min_quality.toString());
    if (options.limit !== undefined) params.append('limit', options.limit.toString());
    
    return apiService.get<SavedPatternsResponse>(`/vcp/saved?${params.toString()}`);
  }

  async savePattern(pattern: VCPPattern): Promise<{ success: boolean; pattern?: VCPPattern; error?: string }> {
    return apiService.post('/vcp/save', pattern);
  }

  async getVCPCriteria(): Promise<VCPCriteriaResponse> {
    return apiService.get<VCPCriteriaResponse>('/vcp/criteria');
  }
}

export const vcpService = new VCPService();
